import { Check, ChevronUp, Clock, ShieldCheck, Tag } from "lucide-react"

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { cn } from "@/lib/utils"

// Price summary for the wizard (CONFIG-SCHEMA §4).
//
// The rail has FOUR variants (audit E). The default "estimate" variant is the
// itemized money rail used from the hub onward, where the design shows totals.
// The entry select stage uses the design's qualitative variants instead:
//
//   variant="step1"        — "No charge to post" / $0 today / shield footer
//   variant="bundleSelect" — bundle name eyebrow, C4 "From $X", includes as ✓ rows
//   variant="byoSelect"    — "Your project" / N services / each selected service row
//   variant="estimate"     — the itemized $ rail (default), server total authoritative
//
// Every variant shows the always-on free-to-post reassurance + trust footer.
//
// RESPONSIVE (P8-H5 "Footlights", design archetype C): at or above 1081px the
// rail is the card stack below, unchanged. At or below 1080px the card stack
// leaves the layout and the rail becomes a FIXED bottom bar carrying that
// variant's truthful headline; tapping (or keyboard-activating) the bar opens
// the same card as a DS bottom Sheet over the primitive's scrim. No delivery
// figure is ever shown on the bar — the app has none, and the rail's existing
// "delivery estimate" footer line stays the only delivery claim (inside the
// sheet).
//
// estimate props:
//   lines        — [{ label, amount, muted?, sub? }]   line items
//   total        — number  (the engine overrides estimate with server total)
//   isRange      — bool     true => prefix the total with `fromLabel`
//   fromLabel    — string   default "From"
//   discountNote — string   shown when a discount is applied
//   note         — optional small print under the card (delivery estimate)
//
// bundleSelect / byoSelect props:
//   eyebrow      — headline eyebrow (bundle name)
//   fromPrice    — the C4 "From $X" price string (bundleSelect)
//   checklist    — [string] includes rows rendered as ✓ (bundleSelect)
//   count        — selected-service count (byoSelect)
//   serviceRows  — [{ name, price }] selected services (byoSelect)
//   emptyText    — empty-state copy when nothing is picked yet
const money = (n) =>
  typeof n === "number"
    ? `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : n

// The always-on reassurance line inside the headline card + the trust footer
// below it (restored design copy, audit E).
const REASSURANCE = "No charge to post. You pay once a writer is attached and you start."
const TRUST = "Free to post · drafts & revisions until you approve · you keep all rights to your book."

// ── shared responsive chrome (archetype C) ───────────────────────────────────
//
// Non-overlapping bands: the card stack owns "hide at or below 1080" and the
// bar owns "hide at or above 1081", so no two rules ever contest `display` and
// the Tailwind v4 arbitrary-variant emission order is irrelevant here. The
// literal is the EXCLUSIVE edge — v4 emits `max-[1081px]` as
// `@media (width < 1081)`, i.e. exactly "at or below 1080".
export const RAIL_CARD_BAND = "max-[1081px]:hidden"
export const RAIL_BAR_BAND = "min-[1081px]:hidden"

// The bar's own height plus breathing room. Wizard content wells add this as
// bottom padding below 1081 so the footer actions clear the fixed bar (design
// prototype `.jf-grid{padding-bottom:104px}`).
export const RAIL_BAR_CLEARANCE = "max-[1081px]:pb-[104px]"

// The expanded breakdown's scroll cap (prototype `.jf-pc-body{max-height:46vh}`
// — the bundle breakdown is long enough to run off a phone).
export const RAIL_SHEET_BODY_CAP = "max-h-[46vh] overflow-y-auto"
const SHEET_BODY_CAP = RAIL_SHEET_BODY_CAP

function TrustFooter() {
  return (
    <p className="flex gap-2 px-1 text-[11.5px] leading-relaxed text-muted-foreground">
      <ShieldCheck className="mt-px size-3.5 shrink-0" strokeWidth={1.9} />
      <span>{TRUST}</span>
    </p>
  )
}

function ClockFooter({ children }) {
  return (
    <div className="flex items-center gap-2 border-t border-border bg-accent px-5 py-3 text-[12px] font-semibold text-muted-foreground">
      <Clock className="size-3.5 shrink-0" strokeWidth={2} />
      <span>{children}</span>
    </div>
  )
}

// The card's headline block. Inside the sheet the eyebrow + big figure collapse
// away (the bar directly above already carries them) and only the reassurance
// copy remains, exactly as the prototype's `.jf-rail.open .jf-pc-top` does.
function Headline({ inSheet, eyebrow, figure, sub, reassurance }) {
  return (
    <div className={cn("border-b border-border bg-accent px-5 py-4", inSheet && "pr-11")}>
      {!inSheet && (
        <>
          <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
            {eyebrow}
          </div>
          {figure}
        </>
      )}
      {sub && (
        <div className={cn("text-[11.5px] leading-snug text-muted-foreground", !inSheet && "mt-2.5")}>
          {sub}
        </div>
      )}
      {reassurance && (
        <div
          className={cn(
            "flex items-start gap-2 text-[11.5px] leading-snug text-muted-foreground",
            (!inSheet || sub) && "mt-2.5"
          )}
        >
          <ShieldCheck className="mt-px size-3.5 shrink-0" strokeWidth={2} />
          <span>{reassurance}</span>
        </div>
      )}
    </div>
  )
}

// The fixed mobile bar + its DS bottom Sheet. Shared by every rail seam,
// including the standalone pricing calculator (which passes a total-only bar).
//
// The bar itself stays OUTSIDE the sheet portal so the primitive's focus
// containment has nothing to fight; the primitive supplies the overlay,
// Escape-close, scrim-close and focus-return-to-trigger untouched. The
// breakdown is pure props, so opening or closing the sheet cannot lose or reset
// any wizard or estimate state.
export function RailBottomBar({ eyebrow, from, value, suffix, hint, label = "Show price breakdown", children }) {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="rail-bar"
          aria-label={label}
          className={cn(
            "fixed inset-x-0 bottom-0 z-40 flex w-full items-center gap-3.5 bg-[image:var(--railbar-bg)] px-[18px] pt-3 text-left text-railbar-foreground outline-none",
            "pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]",
            "focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-inset",
            RAIL_BAR_BAND
          )}
        >
          <span className="min-w-0">
            <span className="block truncate text-[10px] font-bold uppercase tracking-[0.09em] text-railbar-foreground/55">
              {eyebrow}
            </span>
            <span className="flex items-baseline gap-1.5 whitespace-nowrap font-display text-[20px] font-bold leading-[1.15] tracking-[-0.5px]">
              {from && <span className="text-[11.5px] font-semibold text-railbar-foreground/60">{from}</span>}
              {value}
              {suffix && (
                <span className="text-[11.5px] font-semibold text-railbar-foreground/60">{suffix}</span>
              )}
            </span>
          </span>

          {hint && (
            <span className="ml-auto whitespace-nowrap text-[11.5px] font-semibold text-railbar-foreground/72 max-[561px]:hidden">
              {hint}
            </span>
          )}

          <span
            className={cn(
              "grid size-[30px] shrink-0 place-items-center rounded-[9px] bg-railbar-foreground/12",
              !hint && "ml-auto"
            )}
          >
            <ChevronUp className="size-[15px]" strokeWidth={2.2} />
          </span>
        </button>
      </SheetTrigger>

      <SheetContent
        side="bottom"
        data-testid="rail-sheet"
        className="max-h-[92vh] gap-0 overflow-hidden rounded-t-2xl border-t-0 bg-transparent p-0 shadow-none"
      >
        <SheetTitle className="sr-only">Price breakdown</SheetTitle>
        <SheetDescription className="sr-only">
          The full breakdown behind the total shown in the bar.
        </SheetDescription>
        {children}
      </SheetContent>
    </Sheet>
  )
}

// ── entry step 1: "No charge to post" ────────────────────────────────────────
function Step1Card({ inSheet }) {
  return (
    <div className="flex flex-col gap-3.5">
      <div className={cn("overflow-hidden rounded-2xl border border-border bg-card", inSheet && "rounded-b-none border-b-0")}>
        <Headline
          inSheet={inSheet}
          eyebrow="No charge to post"
          figure={
            <div className="mt-1 flex items-baseline gap-2 font-display text-[30px] font-bold leading-none tracking-tight text-foreground">
              {money(0)}
              <span className="text-[13px] font-semibold text-muted-foreground">today</span>
            </div>
          }
          reassurance="Posting a project is always free. You only pay once you choose a freelancer and start the work."
        />
        <ClockFooter>Drafts &amp; revisions until you approve, you keep all rights.</ClockFooter>
      </div>
      {!inSheet && <TrustFooter />}
    </div>
  )
}

// ── entry step 2 (bundle): includes checklist, no $ ──────────────────────────
function BundleSelectCard({ eyebrow, fromPrice, checklist, emptyText, inSheet }) {
  const rows = Array.isArray(checklist) ? checklist : []
  const picked = !!eyebrow

  return (
    <div className="flex flex-col gap-3.5">
      <div className={cn("overflow-hidden rounded-2xl border border-border bg-card", inSheet && "rounded-b-none border-b-0")}>
        <Headline
          inSheet={inSheet}
          eyebrow={eyebrow || "Bundle estimate"}
          figure={
            <div className="mt-1 flex items-baseline gap-2 font-display text-[26px] font-bold leading-none tracking-tight text-foreground">
              {picked && fromPrice ? fromPrice : "—"}
            </div>
          }
          sub={
            picked
              ? "Includes a baseline manuscript. Final price scales with your book's word count."
              : emptyText
          }
          reassurance={REASSURANCE}
        />

        {picked && rows.length > 0 && (
          <div data-testid={inSheet ? "rail-sheet-body" : undefined} className={cn("px-5 py-3", inSheet && SHEET_BODY_CAP)}>
            {rows.map((line, i) => (
              <div key={i} className="flex items-center justify-between gap-3 py-1.5 text-[12.5px]">
                <span className="text-muted-foreground">{line}</span>
                <Check className="size-3.5 shrink-0 text-done" strokeWidth={2.6} />
              </div>
            ))}
          </div>
        )}

        <ClockFooter>Delivery estimate set when you configure the writing.</ClockFooter>
      </div>
      {!inSheet && <TrustFooter />}
    </div>
  )
}

// ── entry step 2 (build-your-own): selected services list ────────────────────
function ByoSelectCard({ count = 0, serviceRows, emptyText, inSheet }) {
  const rows = Array.isArray(serviceRows) ? serviceRows : []

  return (
    <div className="flex flex-col gap-3.5">
      <div className={cn("overflow-hidden rounded-2xl border border-border bg-card", inSheet && "rounded-b-none border-b-0")}>
        <Headline
          inSheet={inSheet}
          eyebrow="Your project"
          figure={
            <div className="mt-1 flex items-baseline gap-2 font-display text-[30px] font-bold leading-none tracking-tight text-foreground">
              {count}
              <span className="text-[13px] font-semibold text-muted-foreground">
                {count === 1 ? "service" : "services"}
              </span>
            </div>
          }
          sub="Each service is priced individually. You'll see the exact figure as you set up each one."
          reassurance="No charge to post. You pay per service, once a freelancer is attached."
        />

        <div data-testid={inSheet ? "rail-sheet-body" : undefined} className={cn("px-5 py-3", inSheet && SHEET_BODY_CAP)}>
          {rows.length > 0 ? (
            rows.map((row, i) => (
              <div key={i} className="flex items-baseline justify-between gap-3 py-1.5 text-[12.5px]">
                <div className="text-muted-foreground">
                  {row.name}
                  {row.price && <span className="mt-0.5 block text-[11px] text-muted-foreground/70">{row.price}</span>}
                </div>
                <span className="whitespace-nowrap font-display text-[12.5px] font-bold text-foreground">Set up</span>
              </div>
            ))
          ) : (
            <p className="py-1.5 text-[12.5px] text-muted-foreground">{emptyText}</p>
          )}
        </div>

        <ClockFooter>Delivery estimates set per service.</ClockFooter>
      </div>
      {!inSheet && <TrustFooter />}
    </div>
  )
}

// ── the itemized money rail (default) ────────────────────────────────────────
function EstimateCard({ lines, total, isRange, fromLabel, discountNote, note, inSheet }) {
  const totalText = money(total)

  return (
    <div className="flex flex-col gap-3.5">
      <div className={cn("overflow-hidden rounded-2xl border border-border bg-card", inSheet && "rounded-b-none border-b-0")}>
        <Headline
          inSheet={inSheet}
          eyebrow="Estimated total"
          figure={
            <div className="mt-1 flex items-baseline gap-2 font-display text-[30px] font-bold leading-none tracking-tight text-foreground">
              {isRange && (
                <span className="text-[13px] font-semibold text-muted-foreground">{fromLabel}</span>
              )}
              {totalText}
            </div>
          }
          reassurance={REASSURANCE}
        />

        {/* line items */}
        <div data-testid={inSheet ? "rail-sheet-body" : undefined} className={cn("px-5 py-4", inSheet && SHEET_BODY_CAP)}>
          {lines.length > 0 ? (
            lines.map((line, i) => (
              <div key={i} className="flex items-baseline justify-between gap-3 py-1.5 text-[12.5px]">
                <div className={cn(line.muted ? "text-muted-foreground/70" : "text-muted-foreground")}>
                  {line.label}
                  {line.sub && (
                    <span className="mt-0.5 block text-[11px] text-muted-foreground/70">{line.sub}</span>
                  )}
                </div>
                <div
                  className={cn(
                    "whitespace-nowrap font-display font-bold",
                    line.muted ? "text-muted-foreground" : "text-foreground"
                  )}
                >
                  {money(line.amount)}
                </div>
              </div>
            ))
          ) : (
            <p className="py-1.5 text-[12.5px] text-muted-foreground">
              Make your selections to see an estimate.
            </p>
          )}

          {discountNote && (
            <div className="flex items-center justify-between gap-3 py-1.5 text-[12.5px]">
              <div className="flex items-center gap-1.5 text-done">
                <Tag className="size-3.5" strokeWidth={2} />
                {discountNote}
              </div>
            </div>
          )}

          <div className="my-2 h-px bg-border" />

          <div className="flex items-baseline justify-between pt-1">
            <div className="text-[13px] font-bold text-foreground">Estimate</div>
            <div className="font-display text-[20px] font-bold text-foreground">
              {isRange && <span className="mr-1 text-[13px] font-semibold text-muted-foreground">from</span>}
              {totalText}
            </div>
          </div>
        </div>

        {note && <ClockFooter>{note}</ClockFooter>}
      </div>

      {!inSheet && <TrustFooter />}
    </div>
  )
}

// The bar's per-variant truth. Each entry restates exactly what that variant's
// headline card already says — never a figure the app does not have.
function barContent({ variant, total, isRange, fromLabel, eyebrow, fromPrice, count }) {
  if (variant === "step1") {
    return { eyebrow: "No charge to post", value: money(0), suffix: "today" }
  }
  if (variant === "bundleSelect") {
    const picked = !!eyebrow
    return { eyebrow: eyebrow || "Bundle estimate", value: picked && fromPrice ? fromPrice : "—" }
  }
  if (variant === "byoSelect") {
    const n = count ?? 0
    return { eyebrow: "Your project", value: n, suffix: n === 1 ? "service" : "services" }
  }
  return { eyebrow: "Estimated total", from: isRange ? fromLabel : null, value: money(total) }
}

export function PriceRail({
  variant = "estimate",
  lines = [],
  total = 0,
  isRange = false,
  fromLabel = "From",
  discountNote,
  note,
  // entry-variant props
  eyebrow,
  fromPrice,
  checklist,
  count,
  serviceRows,
  emptyText,
}) {
  const card = (inSheet) => {
    if (variant === "step1") return <Step1Card inSheet={inSheet} />
    if (variant === "bundleSelect") {
      return (
        <BundleSelectCard
          eyebrow={eyebrow}
          fromPrice={fromPrice}
          checklist={checklist}
          emptyText={emptyText}
          inSheet={inSheet}
        />
      )
    }
    if (variant === "byoSelect") {
      return <ByoSelectCard count={count} serviceRows={serviceRows} emptyText={emptyText} inSheet={inSheet} />
    }
    return (
      <EstimateCard
        lines={lines}
        total={total}
        isRange={isRange}
        fromLabel={fromLabel}
        discountNote={discountNote}
        note={note}
        inSheet={inSheet}
      />
    )
  }

  const bar = barContent({ variant, total, isRange, fromLabel, eyebrow, fromPrice, count })

  return (
    <>
      <div data-testid="rail-card" className={RAIL_CARD_BAND}>{card(false)}</div>
      <RailBottomBar {...bar} hint="Free to post">
        {card(true)}
      </RailBottomBar>
    </>
  )
}
